import React, { useState, useEffect } from "react";
import { Modal } from "./ui";
import client from "../lib/api";
import { toast } from "sonner";
import { num, money } from "../lib/holdingsMath";
import { Gauge, Plus, Trash2, Loader2, TrendingUp, BellRing } from "lucide-react";

// Owner-chosen re-appraisal reminder window. Reads/writes /settings.appraisal_reminder_months.
export function AppraisalReminderSetting() {
  const [months, setMonths] = useState(null);
  useEffect(() => { client.get("/settings").then(({ data }) => setMonths(data?.appraisal_reminder_months ?? 12)).catch(() => setMonths(12)); }, []);
  if (months === null) return null;
  const save = async (v) => {
    setMonths(v);
    try { await client.put("/settings", { appraisal_reminder_months: v }); toast.success(v === 0 ? "Re-appraisal reminders off." : `Reminder set to every ${v} months.`); }
    catch (e) { toast.error("Could not save."); }
  };
  return (
    <div className="inline-flex items-center gap-1.5 rounded border border-black/15 px-2 py-1 text-[11px] dark:border-white/15" data-testid="appraisal-reminder-setting" title="Email me when an asset hasn't been re-appraised within this window">
      <BellRing size={13} className="opacity-70" /> <span className="opacity-70">Re-appraisal reminder</span>
      <select value={months} onChange={(e) => save(Number(e.target.value))} data-testid="appraisal-reminder-select" className="bg-transparent font-semibold focus:outline-none">
        <option value={0}>Off</option>
        <option value={6}>Every 6 months</option>
        <option value={12}>Every 12 months</option>
        <option value={24}>Every 24 months</option>
      </select>
    </div>
  );
}


const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[12px] dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";
const today = () => new Date().toISOString().slice(0, 10);

function Spark({ points }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => num(p.value));
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const W = 160, H = 34;
  const step = W / (vals.length - 1);
  const d = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(H - ((v - min) / span) * (H - 4) - 2).toFixed(1)}`).join(" ");
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <svg width={W} height={H} className="overflow-visible" data-testid="apr-spark">
      <path d={d} fill="none" stroke={up ? "#16a34a" : "#ef4444"} strokeWidth="1.6" />
    </svg>
  );
}

// Per-asset valuation: FMV (from latest dated appraisal) + Replacement/Insurance value +
// appraised-by note + a running dated valuation history with a mini-trend. Holdings only.
export function AppraisalButton({ holding, reload }) {
  const id = holding.id;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(holding.appraisal_history || []);
  const [fmv, setFmv] = useState(num(holding.fmv));
  const [replacement, setReplacement] = useState(String(holding.replacement_value ?? ""));
  const [appraisedBy, setAppraisedBy] = useState(holding.appraised_by || "");
  const [draft, setDraft] = useState({ date: today(), value: "", source: "" });

  const openModal = () => {
    setHist(holding.appraisal_history || []);
    setFmv(num(holding.fmv));
    setReplacement(String(holding.replacement_value ?? ""));
    setAppraisedBy(holding.appraised_by || "");
    setDraft({ date: today(), value: "", source: "" });
    setOpen(true);
  };

  const addValuation = async () => {
    if (!draft.date) return toast.error("Pick a valuation date.");
    setBusy(true);
    try {
      const { data } = await client.post(`/holdings/${id}/valuations`, {
        date: draft.date, value: num(draft.value), source: draft.source });
      setHist(data.appraisal_history || []);
      setFmv(num(data.fmv));
      setDraft({ date: today(), value: "", source: "" });
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add valuation."); }
    setBusy(false);
  };

  const delValuation = async (vid) => {
    setBusy(true);
    try {
      const { data } = await client.delete(`/holdings/${id}/valuations/${vid}`);
      setHist(data.appraisal_history || []);
      setFmv(num(data.fmv));
      reload && reload();
    } catch (e) { toast.error("Could not remove valuation."); }
    setBusy(false);
  };

  const saveScalars = async () => {
    setBusy(true);
    try {
      await client.put(`/holdings/${id}`, { data: { replacement_value: num(replacement), appraised_by: appraisedBy } });
      toast.success("Saved.");
      reload && reload();
    } catch (e) { toast.error("Could not save."); }
    setBusy(false);
  };

  return (
    <>
      <button type="button" onClick={openModal} title="Appraisal & valuation history" data-testid={`apr-open-${id}`}
        className="inline-flex items-center gap-1 rounded border border-black/15 px-1.5 py-1 text-[11px] hover:bg-black/[0.04] dark:border-white/15 dark:hover:bg-white/[0.06]">
        <Gauge size={13} />{fmv ? <span className="tabular-nums">{money(fmv)}</span> : <span className="opacity-70">Value</span>}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Appraisal & valuation history" testid={`apr-modal-${id}`} wide>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <div>
            <span className={LL}>Current FMV (latest appraisal)</span>
            <div className="rounded border border-black/10 bg-black/[0.03] px-2 py-1.5 text-sm font-semibold tabular-nums dark:border-white/10 dark:bg-white/[0.04]" data-testid={`apr-fmv-${id}`}>
              {fmv ? money(fmv) : "—"}{holding.fmv_date ? <span className="ms-1 text-[10px] font-normal opacity-55">as of {holding.fmv_date}</span> : null}
            </div>
          </div>
          <label className="block"><span className={LL}>Replacement / insurance value ($)</span>
            <input className={`${II} tabular-nums`} type="number" value={replacement} onChange={(e) => setReplacement(e.target.value)} onBlur={saveScalars} data-testid={`apr-replacement-${id}`} />
          </label>
          <label className="block"><span className={LL}>Appraised by / source</span>
            <input className={II} value={appraisedBy} onChange={(e) => setAppraisedBy(e.target.value)} onBlur={saveScalars} placeholder="e.g. Sotheby's, 2026" data-testid={`apr-appraisedby-${id}`} />
          </label>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <span className="font-head text-sm">Valuation history</span>
          {hist.length >= 2 && <span className="ms-auto flex items-center gap-1 text-[10px] opacity-60"><TrendingUp size={12} /> trend</span>}
          {hist.length >= 2 && <Spark points={[...hist].sort((a, b) => String(a.date).localeCompare(String(b.date)))} />}
        </div>

        <div className="mb-3 grid gap-2 sm:grid-cols-4" data-testid={`apr-add-row-${id}`}>
          <label className="block"><span className={LL}>Date *</span>
            <input className={II} type="date" value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} data-testid={`apr-hist-date-${id}`} /></label>
          <label className="block"><span className={LL}>FMV ($)</span>
            <input className={`${II} tabular-nums`} type="number" value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} data-testid={`apr-hist-value-${id}`} /></label>
          <label className="block"><span className={LL}>Source</span>
            <input className={II} value={draft.source} onChange={(e) => setDraft((d) => ({ ...d, source: e.target.value }))} placeholder="appraiser / basis" data-testid={`apr-hist-source-${id}`} /></label>
          <div className="flex items-end">
            <button onClick={addValuation} disabled={busy} data-testid={`apr-add-${id}`}
              className="flex items-center gap-1 rounded bg-[var(--primary)] px-2 py-1.5 text-[12px] font-semibold text-white disabled:opacity-40">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add</button>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-black/10 dark:border-white/10">
          <table className="w-full text-[12px]">
            <thead className="bg-black/5 dark:bg-white/10"><tr>
              <th className="px-2 py-1.5 text-start font-medium">Date</th>
              <th className="px-2 py-1.5 text-end font-medium">FMV</th>
              <th className="px-2 py-1.5 text-start font-medium">Source</th>
              <th className="px-2 py-1.5"></th>
            </tr></thead>
            <tbody data-testid={`apr-hist-list-${id}`}>
              {hist.length === 0 ? (
                <tr><td colSpan={4} className="px-2 py-3 text-center opacity-55">No valuations yet. Add one above.</td></tr>
              ) : [...hist].sort((a, b) => String(b.date).localeCompare(String(a.date))).map((v) => (
                <tr key={v.id} className="border-t border-black/5 dark:border-white/5" data-testid={`apr-hist-row-${v.id}`}>
                  <td className="px-2 py-1.5">{v.date}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums">{money(v.value)}</td>
                  <td className="px-2 py-1.5 opacity-75">{v.source || "—"}</td>
                  <td className="px-2 py-1.5 text-end"><button onClick={() => delValuation(v.id)} disabled={busy} data-testid={`apr-del-${v.id}`} className="text-black/40 hover:text-red-500 dark:text-white/40"><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] opacity-55">The most recent appraisal sets this asset's current FMV, which the Consolidated Net-Worth Statement uses in place of gross value.</p>
      </Modal>
    </>
  );
}
